import {afterNextRender, ChangeDetectionStrategy, Component, inject, signal, ViewEncapsulation} from '@angular/core';
import {SeasonalThemeService} from '@services/seasonal-theme.service';

interface Flake {
	id: number;
	left: number;
	size: number;
	opacity: number;
	duration: number;
	delay: number;
	sway: number;
	swayDuration: number;
	spin: number;
}

const FLAKE_COUNT = 10;

const rand = (min: number, max: number): number => min + Math.random() * (max - min);

/**
 * Quelques flocons (Noël) qui tombent chacun à leur rythme : position, taille, vitesse,
 * balancement et rotation tirés au hasard, et retirés à nouveau à chaque chute.
 * Rendu côté navigateur uniquement (le hasard ne doit pas différer entre SSR et client).
 */
@Component({
	selector: 'peps-seasonal-snow',
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
	template: `
		@if (flakes().length) {
			<div class="peps-snow" aria-hidden="true">
				@for (f of flakes(); track f.id) {
					<span
						class="peps-snow-fall"
						[style.left.%]="f.left"
						[style.animationDuration.s]="f.duration"
						[style.animationDelay.s]="f.delay"
						(animationiteration)="onIteration($event, f.id)"
					>
						<span class="peps-snow-sway" [style.--sway]="f.sway + 'px'" [style.animationDuration.s]="f.swayDuration">
							<span
								class="peps-snow-flake"
								[style.width.px]="f.size"
								[style.height.px]="f.size"
								[style.opacity]="f.opacity"
								[style.animationDuration.s]="f.spin"
							></span>
						</span>
					</span>
				}
			</div>
		}
	`,
	styles: `
		.peps-snow { position: fixed; inset: 0; overflow: hidden; pointer-events: none; z-index: 40; }
		.peps-snow-fall {
			position: absolute;
			top: -30px;
			animation: peps-snow-fall linear infinite;
			will-change: transform;
		}
		.peps-snow-sway {
			display: block;
			animation: peps-snow-sway ease-in-out infinite alternate;
		}
		.peps-snow-flake {
			display: block;
			background: url("data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 24 24%22%3E%3Cpath d=%22M12 12L12.00 1.50M12.00 6.75L8.54 4.75M12.00 6.75L15.46 4.75M12.00 4.12L9.64 2.76M12.00 4.12L14.36 2.76M12 12L21.09 6.75M16.55 9.38L16.55 5.38M16.55 9.38L20.00 11.37M18.82 8.06L18.82 5.33M18.82 8.06L21.18 9.43M12 12L21.09 17.25M16.55 14.62L20.00 12.63M16.55 14.62L16.55 18.62M18.82 15.94L21.18 14.57M18.82 15.94L18.82 18.67M12 12L12.00 22.50M12.00 17.25L15.46 19.25M12.00 17.25L8.54 19.25M12.00 19.88L14.36 21.24M12.00 19.88L9.64 21.24M12 12L2.91 17.25M7.45 14.62L7.45 18.62M7.45 14.62L4.00 12.63M5.18 15.94L5.18 18.67M5.18 15.94L2.82 14.57M12 12L2.91 6.75M7.45 9.38L4.00 11.37M7.45 9.38L7.45 5.38M5.18 8.06L2.82 9.43M5.18 8.06L5.18 5.33%22 fill=%22none%22 stroke=%22white%22 stroke-width=%221.4%22 stroke-linecap=%22round%22/%3E%3C/svg%3E") center / contain no-repeat;
			animation: peps-snow-spin linear infinite;
		}
		@keyframes peps-snow-fall {
			from { transform: translate3d(0, 0, 0); }
			to { transform: translate3d(0, calc(100vh + 60px), 0); }
		}
		@keyframes peps-snow-sway {
			from { transform: translateX(calc(var(--sway, 20px) * -1)); }
			to { transform: translateX(var(--sway, 20px)); }
		}
		@keyframes peps-snow-spin {
			to { transform: rotate(360deg); }
		}
		@media (prefers-reduced-motion: reduce) {
			.peps-snow { display: none; }
		}
	`,
})
export class SeasonalSnowComponent {
	private nextId = 0;
	protected readonly flakes = signal<Flake[]>([]);

	constructor() {
		const seasonalTheme = inject(SeasonalThemeService);
		// Après l'hydratation : le serveur ne rend aucun flocon, le client ne doit rien ajouter avant.
		afterNextRender(() => {
			if (seasonalTheme.theme() !== 'christmas') return;
			// Départs étalés : les flocons sont déjà à des hauteurs différentes au chargement.
			this.flakes.set(Array.from({length: FLAKE_COUNT}, () => {
				const flake = this.createFlake();
				flake.delay = -rand(0, flake.duration);
				return flake;
			}));
		});
	}

	/** Un flocon qui a fini sa chute est remplacé par un nouveau, tiré au hasard. */
	protected onIteration(event: AnimationEvent, id: number): void {
		if (event.animationName !== 'peps-snow-fall') return;
		this.flakes.update(list => list.map(f => f.id === id ? this.createFlake() : f));
	}

	private createFlake(): Flake {
		return {
			id: this.nextId++,
			left: rand(0, 100),
			size: rand(8, 15),
			opacity: rand(0.45, 0.9),
			duration: rand(8, 16),
			delay: rand(0, 5),
			sway: rand(12, 45),
			swayDuration: rand(2.5, 5.5),
			spin: rand(6, 14),
		};
	}
}
